import { Widget } from '../core/domain/widget';
import { WidgetFieldConfig } from '../core/domain/widget-field-config';

/** Category-dependent booking fields, as they sit in the form groups. */
export interface BookingSelections {
  addServices: WidgetFieldConfig[];
  addServicesPrice: number;
  addServicesTime: number;
  addInfo: WidgetFieldConfig[];
  addInfoPrice: number;
  addInfoTime: number;
  propertyTypeName: string;
  propertyTypeBasePrice: number;
  propertyTypeBaseTime: number;
  propertySizeName: string;
  propertySizeBasePrice: number;
  propertySizeBaseTime: number;
  propertySizeArea: number;
}

/** Sums price and time of the selected add-on / add-info items (same semantics as the page loops). */
export function sumAddFields(fields: WidgetFieldConfig[]): {
  price: number;
  time: number;
} {
  let price = 0;
  let time = 0;

  for (const field of fields) {
    price += parseFloat(field.addPrice as any);
  }

  for (const field of fields) {
    time += parseFloat((field.addTime || 0) as any);
  }

  return { price, time };
}

/**
 * Maps previously selected items onto the current catalog items. Match by id,
 * falling back to the trimmed name when either side lacks an id. Each previous
 * item claims at most one catalog item. Returns catalog objects in catalog order.
 */
export function remapItems(
  previous: WidgetFieldConfig[] | null | undefined,
  items: WidgetFieldConfig[] | null | undefined,
): WidgetFieldConfig[] {
  if (!Array.isArray(items)) return [];
  if (!Array.isArray(previous) || previous.length === 0) return [];

  const hasId = (item: WidgetFieldConfig) =>
    typeof item?.id === 'string' && item.id !== '';
  const nameOf = (item: WidgetFieldConfig) =>
    typeof item?.name === 'string' ? item.name.trim() : undefined;

  const matches = (p: WidgetFieldConfig, c: WidgetFieldConfig): boolean => {
    if (hasId(p) && hasId(c)) return p.id === c.id;
    const pn = nameOf(p);
    return pn !== undefined && pn === nameOf(c);
  };

  const claimed = new Set<number>();

  for (const p of previous) {
    const index = items.findIndex((c, i) => !claimed.has(i) && matches(p, c));
    if (index > -1) claimed.add(index);
  }

  return items.filter((_, i) => claimed.has(i));
}

/** Exact name lookup; radio values are exact names. */
export function findByName(
  items: WidgetFieldConfig[] | null | undefined,
  name: unknown,
): WidgetFieldConfig | undefined {
  if (!Array.isArray(items)) return undefined;
  return items.find((item) => item.name === name);
}

/** Price and time of the "area" property-size method. */
export function areaPricing(
  area: number,
  widget: Widget,
): { price: number; time: number } {
  if (!area) return { price: 0, time: 0 };

  const timeFactor = widget.propertySizeAreaTime || 0;
  const priceFactor = widget.propertySizeAreaPrice || 0;

  return {
    price: parseFloat((area * priceFactor).toFixed(2)),
    time: parseFloat(((area * timeFactor) / 60).toFixed(2)),
  };
}

function areaSelections(
  current: BookingSelections,
  widget: Widget,
): Pick<
  BookingSelections,
  | 'propertySizeName'
  | 'propertySizeBasePrice'
  | 'propertySizeBaseTime'
  | 'propertySizeArea'
> {
  const area = current.propertySizeArea;
  const pricing = areaPricing(area, widget);

  return {
    propertySizeName: area > 0 ? `${area} sq. feet` : '',
    propertySizeBasePrice: pricing.price,
    propertySizeBaseTime: pricing.time,
    propertySizeArea: area,
  };
}

/** Category switched: drop every category-dependent selection. */
export function clearedSelections(
  current: BookingSelections,
  widget: Widget,
): BookingSelections {
  const size =
    widget.propertySizePricingMethod === 'area'
      ? areaSelections(current, widget)
      : {
          propertySizeName: '',
          propertySizeBasePrice: 0,
          propertySizeBaseTime: 0,
          propertySizeArea: current.propertySizeArea,
        };

  return {
    addServices: [],
    addServicesPrice: 0,
    addServicesTime: 0,
    addInfo: [],
    addInfoPrice: 0,
    addInfoTime: 0,
    propertyTypeName: '',
    propertyTypeBasePrice: 0,
    propertyTypeBaseTime: 0,
    ...size,
  };
}

/** Same category emitted again (live edit): keep what still exists, re-priced from the new data. */
export function remappedSelections(
  current: BookingSelections,
  widget: Widget,
): BookingSelections {
  const addServices = remapItems(current.addServices, widget.addServices);
  const addServicesSum = sumAddFields(addServices);
  const addInfo = remapItems(current.addInfo, widget.addInfo);
  const addInfoSum = sumAddFields(addInfo);

  const type = findByName(widget.propertyType, current.propertyTypeName);

  let size: Pick<
    BookingSelections,
    | 'propertySizeName'
    | 'propertySizeBasePrice'
    | 'propertySizeBaseTime'
    | 'propertySizeArea'
  >;

  if (widget.propertySizePricingMethod === 'area') {
    size = areaSelections(current, widget);
  } else {
    const tier = findByName(widget.propertySize, current.propertySizeName);
    size = {
      propertySizeName: tier ? current.propertySizeName : '',
      propertySizeBasePrice: tier ? tier.addPrice : 0,
      propertySizeBaseTime: tier ? tier.addTime || 0 : 0,
      propertySizeArea: current.propertySizeArea,
    };
  }

  return {
    addServices,
    addServicesPrice: addServicesSum.price,
    addServicesTime: addServicesSum.time,
    addInfo,
    addInfoPrice: addInfoSum.price,
    addInfoTime: addInfoSum.time,
    propertyTypeName: type ? current.propertyTypeName : '',
    propertyTypeBasePrice: type ? type.basePrice : 0,
    propertyTypeBaseTime: type ? type.baseTime || 0 : 0,
    ...size,
  };
}

/** Switch clears, a live edit of the same category re-maps. A first load counts as a switch. */
export function reconcileSelections(
  previousWidgetId: string | undefined,
  widget: Widget,
  current: BookingSelections,
): BookingSelections {
  const sameCategory =
    typeof widget.id === 'string' &&
    widget.id !== '' &&
    widget.id === previousWidgetId;

  return sameCategory
    ? remappedSelections(current, widget)
    : clearedSelections(current, widget);
}

/** Order totals; reproduces the pages' former setTotalPrice. */
export function bookingTotals(v: {
  addServicesPrice: any;
  addServicesTime: any;
  travelSurcharge: any;
  propertyTypeBasePrice: any;
  propertyTypeBaseTime: any;
  propertySizeBasePrice: any;
  propertySizeBaseTime: any;
  addInfoPrice: any;
  addInfoTime: any;
}): { totalPrice: number; totalTime: number } {
  const totalPrice =
    parseFloat(v.addServicesPrice) +
    parseFloat(v.travelSurcharge) +
    parseFloat(v.propertyTypeBasePrice) +
    parseFloat(v.propertySizeBasePrice) +
    parseFloat(v.addInfoPrice);

  const totalTime =
    parseFloat(v.addServicesTime || 0) +
    parseFloat(v.propertyTypeBaseTime || 0) +
    parseFloat(v.propertySizeBaseTime || 0) +
    parseFloat(v.addInfoTime || 0);

  return { totalPrice, totalTime };
}

/** True when a property type or size the customer had chosen is gone after reconciling. */
export function lostRequiredPropertySelection(
  current: Pick<BookingSelections, 'propertyTypeName' | 'propertySizeName'>,
  next: Pick<BookingSelections, 'propertyTypeName' | 'propertySizeName'>,
): boolean {
  return (
    (!!current.propertyTypeName && !next.propertyTypeName) ||
    (!!current.propertySizeName && !next.propertySizeName)
  );
}
